// Точка входа React-приложения.
// MantineProvider оборачивает всё, чтобы CSS-переменные темы были везде доступны.
// QueryClient — TanStack Query для server-state (CRUD-страницы, аналитика).
// AuthContext — текущий юзер из /api/auth/me, обновляется при login/logout/смене пароля.

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MantineProvider, createTheme, ColorSchemeScript } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import './styles.css';

import App from './App.jsx';
import { AuthProvider } from './auth/AuthContext.jsx';
import { ApiError } from './api/client.js';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ru');

const theme = createTheme({
    primaryColor: 'blue',
    defaultRadius: 'md',
    fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    headings: {
        fontWeight: '600',
    },
});

// Поведение TanStack Query настроено под коммерческий продукт:
//   - не ретраим 4xx (бесполезно: 401/403/404 не починятся ретраем)
//   - 1 ретрай на 5xx — компенсирует короткие сетевые штормы
//   - staleTime 30s — даёт ощутимый прирост по responsivity при навигации
//     между страницами, но достаточно мал чтобы данные не «застывали»
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: (failureCount, error) => {
                if (error instanceof ApiError) {
                    if (error.status >= 400 && error.status < 500) return false;
                }
                return failureCount < 1;
            },
            staleTime: 30_000,
            refetchOnWindowFocus: false,
        },
        mutations: {
            // Мутации не ретраим вообще — повторять небезопасно (создание юзера и т.п.)
            retry: false,
        },
    },
});

ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <ColorSchemeScript defaultColorScheme="light" />
        <MantineProvider theme={theme} defaultColorScheme="light">
            <ModalsProvider>
                <Notifications position="top-right" autoClose={5000} limit={5} />
                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <AuthProvider>
                            <App />
                        </AuthProvider>
                    </BrowserRouter>
                </QueryClientProvider>
            </ModalsProvider>
        </MantineProvider>
    </React.StrictMode>,
);
